import { getApiClient } from './apiClient';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in')
  .replace(/\/api\/?$/, '');

export interface Report {
  _id: string;
  name: string;
  description?: string;
  type: 'OPERATIONAL' | 'FINANCIAL' | 'CUSTOMER_ANALYTICS' | 'HUB_PERFORMANCE';
  format: 'EXCEL' | 'CSV' | 'JSON' | 'PDF';
  query?: any;
  filters: ReportFilter;
  schedule?: ReportSchedule;
  isTemplate: boolean;
  isPublic: boolean;
  tags: string[];
  cacheEnabled: boolean;
  cacheTTL: number;
  executions: ReportExecution[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportFilter {
  dateFrom?: string;
  dateTo?: string;
  customer?: string;
  company?: string;
  service?: string;
  status?: string;
  hub?: string;
  vendor?: string;
  minValue?: string;
  maxValue?: string;
  paymentMethod?: string;
}

export interface ReportSchedule {
  isActive: boolean;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  time: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  recipients: string[];
  lastRunDate?: string;
  nextRunDate?: string;
}

export interface ReportExecution {
  executionId: string;
  startTime: string;
  endTime?: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  recordsProcessed?: number;
  filePath?: string;
  fileSize?: number;
  errorMessage?: string;
  executedBy?: string;
}

export interface ReportData {
  headers: string[];
  rows: any[][];
  summary?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface ReportGenerationOptions {
  format: 'EXCEL' | 'CSV' | 'JSON' | 'PDF';
  filters: ReportFilter;
  includeCharts?: boolean;
  includeSummary?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

class ReportService {
  private axiosInstance = getApiClient({ baseURL: API_BASE_URL });

  async getReports(params?: {
    page?: number;
    limit?: number;
    type?: string;
    format?: string;
    isTemplate?: boolean;
    isPublic?: boolean;
    tags?: string;
  }): Promise<PaginatedResponse<Report>> {
    const response = await this.axiosInstance.get('/api/reports', {
      params
    });
    return response.data.data;
  }

  async getReport(id: string): Promise<Report> {
    const response = await this.axiosInstance.get(`/api/reports/${id}`);
    return response.data.data;
  }

  async createReport(reportData: Partial<Report>): Promise<Report> {
    const response = await this.axiosInstance.post('/api/reports', reportData);
    return response.data.data;
  }

  async updateReport(id: string, reportData: Partial<Report>): Promise<Report> {
    const response = await this.axiosInstance.put(`/api/reports/${id}`, reportData);
    return response.data.data;
  }

  async deleteReport(id: string): Promise<void> {
    await this.axiosInstance.delete(`/api/reports/${id}`);
  }

  async generateReport(id: string, options: ReportGenerationOptions): Promise<{
    executionId: string;
    message: string;
    downloadUrl: string;
  }> {
    const response = await this.axiosInstance.post(`/api/reports/${id}/generate`, options);
    return response.data.data;
  }

  async downloadReport(id: string, executionId: string): Promise<Blob> {
    const response = await this.axiosInstance.get(`/api/reports/${id}/download/${executionId}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getReportExecutions(id: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<{ executions: ReportExecution[]; total: number }> {
    const response = await this.axiosInstance.get(`/api/reports/${id}/executions`, {
      params
    });
    return response.data.data;
  }

  async getReportTemplates(): Promise<PaginatedResponse<Report>> {
    const response = await this.axiosInstance.get('/api/reports/templates');
    return response.data.data;
  }

  async scheduleReport(id: string): Promise<void> {
    await this.axiosInstance.post(`/api/reports/${id}/schedule`, {});
  }

  async clearReportCache(id: string): Promise<void> {
    await this.axiosInstance.delete(`/api/reports/${id}/cache`);
  }

  // Quick report generation methods
  async generateOperationalReport(filters: ReportFilter): Promise<ReportData> {
    const response = await this.axiosInstance.post('/api/reports/operational', { filters });
    return response.data.data;
  }

  async generateFinancialReport(filters: ReportFilter): Promise<ReportData> {
    const response = await this.axiosInstance.post('/api/reports/financial', { filters });
    return response.data.data;
  }

  async generateCustomerAnalyticsReport(filters: ReportFilter): Promise<ReportData> {
    const response = await this.axiosInstance.post('/api/reports/customer-analytics', { filters });
    return response.data.data;
  }

  async generateHubPerformanceReport(filters: ReportFilter): Promise<ReportData> {
    const response = await this.axiosInstance.post('/api/reports/hub-performance', { filters });
    return response.data.data;
  }

  async getRealTimeMetrics(): Promise<{
    activeAWBs: number;
    todayRevenue: number;
    deliveryRate: number;
    avgProcessingTime: number;
    hubUtilization: number;
    recentActivity: any[];
  }> {
    const response = await this.axiosInstance.get('/api/reports/metrics/realtime');
    return response.data.data;
  }

  async generateAdvancedReport(options: {
    type: string;
    format: string;
    filters: ReportFilter;
    includeCharts?: boolean;
    includeSummary?: boolean;
    includeRawData?: boolean;
    customFileName?: string;
    emailOptions?: any;
    filterOptions?: any;
  }): Promise<{
    executionId: string;
    downloadUrl: string;
    emailSent?: boolean;
  }> {
    const response = await this.axiosInstance.post('/api/reports/advanced', options);
    return response.data.data;
  }

  // Utility methods
  downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  formatReportData(data: ReportData, format: 'CSV' | 'JSON'): string {
    if (format === 'CSV') {
      const csvContent = [
        data.headers.join(','),
        ...data.rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      return csvContent;
    } else if (format === 'JSON') {
      const jsonData = data.rows.map(row => {
        const obj: any = {};
        data.headers.forEach((header, index) => {
          obj[header] = row[index];
        });
        return obj;
      });
      return JSON.stringify({ data: jsonData, summary: data.summary }, null, 2);
    }
    return '';
  }
}

export const reportService = new ReportService();
